import React from "react";
import { getClassName, getInitials } from "@utils";
import { PlaceholderIcon } from "./icons";
import css from "./Avatar.module.scss";

export enum AvatarSize {
  XS = "Xs",
  S = "S",
  M = "M",
  L = "L",
  XL = "Xl",
}

export enum AvatarVariant {
  Default = "Default",
  Prospect = "Prospect",
  User = "User",
  Unassigned = "Unassigned",
}

export interface AvatarProps {
  src?: string;
  name?: string;
  size?: AvatarSize;
  variant?: AvatarVariant;
  placeholder?: React.FC<React.SVGProps<SVGSVGElement>>;
  isLoading?: boolean;
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = AvatarSize.M,
  variant = AvatarVariant.Default,
  placeholder,
  isLoading = false,
  className,
}) => {
  const attrs = {
    avatar: {
      className: getClassName(
        css.avatar,
        css[`avatar${size}`],
        isLoading ? css.avatarLoading : css[`avatar${variant}`],
        className
      ),
    },
    imageWrapper: {
      className: css.imageWrapper,
    },
    image: {
      src: src,
      alt: name,
      className: css.image,
    },
    placeholderIcon: {
      className: css.placeholderIcon,
    },
  };

  if (isLoading) {
    return (
      <div {...attrs.avatar}>
        <div {...attrs.imageWrapper} />
      </div>
    );
  }

  if (src) {
    return (
      <div {...attrs.avatar}>
        <div {...attrs.imageWrapper}>
          <img {...attrs.image} />
        </div>
      </div>
    );
  }

  if (placeholder) {
    const PlaceholderIcon = placeholder;

    return (
      <div {...attrs.avatar}>
        <div {...attrs.imageWrapper}>
          <PlaceholderIcon {...attrs.placeholderIcon} />
        </div>
      </div>
    );
  }

  if (name) {
    return (
      <div {...attrs.avatar}>
        <div {...attrs.imageWrapper}>
          <span>{getInitials(name)}</span>
        </div>
      </div>
    );
  }

  return (
    <div {...attrs.avatar}>
      <div {...attrs.imageWrapper}>
        <PlaceholderIcon {...attrs.placeholderIcon} />
      </div>
    </div>
  );
};

export default Avatar;
